import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../config/api';

const ConveyorItemManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploadMethod, setUploadMethod] = useState('url');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imagePath: '',
    imageWidth: 120,
    speed: 1.5,
    rows: 1,
    height: 120,
    info: '',
    order: 0
  });

  useEffect(() => {
    fetchItems();
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);

  const fetchItems = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/conveyor-items`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error('Error fetching conveyor items:', error);
    } finally {
      setLoading(false);
    }
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          let width = img.width;
          let height = img.height;
          const maxDimension = 1200;
          
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          
          let quality = 0.8;
          const compress = () => {
            canvas.toBlob((blob) => {
              if (blob.size > 512 * 1024 && quality > 0.1) {
                quality -= 0.1;
                compress();
              } else {
                resolve(blob);
              }
            }, 'image/jpeg', quality);
          };
          compress();
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    try {
      setUploading(true);
      let base64String;

      if (file.size > 512 * 1024) {
        console.log('Compressing image...');
        const compressedBlob = await compressImage(file);
        base64String = await blobToBase64(compressedBlob);
      } else {
        base64String = await blobToBase64(file);
      }

      setImagePreview(base64String);
      setImageFile(base64String);
    } catch (error) {
      console.error('Error processing image:', error);
      alert('Error processing image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setUploading(true);
      let imagePath = formData.imagePath;
      let imageSize = 0;

      if (uploadMethod === 'upload' && imageFile) {
        imagePath = imageFile;
        imageSize = Math.round((imageFile.length * 3) / 4);
      }

      if (!imagePath) {
        alert('Please provide an image URL or upload an image');
        setUploading(false);
        return;
      }

      const token = localStorage.getItem('adminToken');
      const url = editingItem 
        ? `${API_BASE_URL}/api/admin/conveyor-items/${editingItem._id}` 
        : `${API_BASE_URL}/api/admin/conveyor-items`;
      const method = editingItem ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          ...formData, 
          imagePath,
          imageSize
        })
      });

      if (response.ok) {
        fetchItems();
        resetForm();
        setShowModal(false);
      } else {
        const error = await response.json();
        alert(error.message || 'Error saving conveyor item');
      }
    } catch (error) {
      console.error('Error saving conveyor item:', error);
      alert('Error saving conveyor item');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      imagePath: item.imagePath,
      imageWidth: item.imageWidth,
      speed: item.speed,
      rows: item.rows,
      height: item.height,
      info: item.info,
      order: item.order
    });
    setImagePreview(item.imagePath);
    setUploadMethod('url');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this conveyor item?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/conveyor-items/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchItems();
      }
    } catch (error) {
      console.error('Error deleting conveyor item:', error);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/conveyor-items/${id}/toggle`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchItems();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      title: '',
      description: '',
      imagePath: '',
      imageWidth: 120,
      speed: 1.5,
      rows: 1,
      height: 120,
      info: '',
      order: 0
    });
    setUploadMethod('url');
    setImageFile(null);
    setImagePreview('');
  };

  const generateRowsOptions = () => {
    const options = [];
    for (let i = 1; i <= 50; i++) {
      options.push(
        <option key={i} value={i}>{i} {i === 1 ? 'row' : 'rows'}</option>
      );
    }
    return options;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      {/* Header - Responsive */}
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">Conveyor Items Management</h1>
        <p className="text-sm sm:text-base text-gray-600">Manage conveyor belt items with up to 50 rows each</p>
      </div>

      {/* Stats Cards - Responsive Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border border-blue-200">
          <div className="text-xs sm:text-sm text-blue-600 font-medium">Total Items</div>
          <div className="text-xl sm:text-2xl font-bold text-blue-700">{items.length}</div>
        </div>
        <div className="bg-green-50 p-3 sm:p-4 rounded-lg border border-green-200">
          <div className="text-xs sm:text-sm text-green-600 font-medium">Active</div>
          <div className="text-xl sm:text-2xl font-bold text-green-700">
            {items.filter(item => item.isActive).length}
          </div>
        </div>
        <div className="bg-purple-50 p-3 sm:p-4 rounded-lg border border-purple-200">
          <div className="text-xs sm:text-sm text-purple-600 font-medium">Total Rows</div>
          <div className="text-xl sm:text-2xl font-bold text-purple-700">
            {items.reduce((sum, item) => sum + (item.rows || 1), 0)}
          </div>
        </div>
        <div className="bg-orange-50 p-3 sm:p-4 rounded-lg border border-orange-200">
          <div className="text-xs sm:text-sm text-orange-600 font-medium">Max Rows</div>
          <div className="text-xl sm:text-2xl font-bold text-orange-700">
            {items.length > 0 ? Math.max(...items.map(item => item.rows || 1)) : 0}
          </div>
        </div>
      </div>

      {/* Add Button - Responsive */}
      <button
        onClick={() => setShowModal(true)}
        className="bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-blue-700 mb-4 sm:mb-6 flex items-center gap-2 text-sm sm:text-base w-full sm:w-auto justify-center transition-colors"
      >
        <span>➕</span> Add New Item
      </button>

      {/* Items Grid - Responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {items.map((item) => (
          <div key={item._id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow">
            <div className="p-3 sm:p-4">
              <div className="flex justify-between items-start mb-3">
                <h3 className="font-bold text-base sm:text-lg text-gray-800 flex-1 pr-2">{item.title}</h3>
                <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                  item.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {item.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              
              <div className="mb-3">
                <img 
                  src={item.imagePath} 
                  alt={item.title}
                  className="w-full h-24 sm:h-32 object-contain bg-gray-50 rounded"
                />
                {item.imageSize > 0 && (
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    Size: {formatFileSize(item.imageSize)}
                  </p>
                )}
              </div>

              <p className="text-gray-600 text-xs sm:text-sm mb-3 line-clamp-2">{item.description}</p>

              <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm mb-3">
                <div className="bg-blue-50 p-2 rounded">
                  <span className="font-medium text-blue-700">Rows:</span>{' '}
                  <span className="text-blue-900">{item.rows}</span>
                </div>
                <div className="bg-purple-50 p-2 rounded">
                  <span className="font-medium text-purple-700">Speed:</span>{' '}
                  <span className="text-purple-900">{item.speed}s</span>
                </div>
                <div className="bg-green-50 p-2 rounded">
                  <span className="font-medium text-green-700">Size:</span>{' '}
                  <span className="text-green-900">{item.imageWidth}×{item.height}</span>
                </div>
                <div className="bg-orange-50 p-2 rounded">
                  <span className="font-medium text-orange-700">Order:</span>{' '}
                  <span className="text-orange-900">{item.order}</span>
                </div>
              </div>

              <div className="mb-3 p-2 bg-gray-50 rounded">
                <span className="font-medium text-gray-700 text-xs sm:text-sm">Info:</span>{' '}
                <span className="text-gray-600 text-xs sm:text-sm">{item.info}</span>
              </div>

              {/* Action Buttons - Responsive */}
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => handleEdit(item)}
                  className="flex-1 bg-blue-500 text-white px-3 sm:px-4 py-2 rounded hover:bg-blue-600 text-xs sm:text-sm transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleToggleStatus(item._id)}
                  className={`flex-1 px-3 sm:px-4 py-2 rounded text-xs sm:text-sm transition-colors ${
                    item.isActive 
                      ? 'bg-yellow-500 hover:bg-yellow-600 text-white' 
                      : 'bg-green-500 hover:bg-green-600 text-white'
                  }`}
                >
                  {item.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDelete(item._id)}
                  className="sm:flex-none bg-red-500 text-white px-3 sm:px-4 py-2 rounded hover:bg-red-600 text-xs sm:text-sm transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {items.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500 text-base sm:text-lg">No conveyor items yet.</p>
          <p className="text-gray-400 text-sm sm:text-base">Create your first item to get started!</p>
        </div>
      )}

      {/* Modal - FULLY RESPONSIVE */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-[9999] overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              resetForm();
              setShowModal(false);
            }
          }}
        >
          <div 
            className="bg-white rounded-lg w-full max-w-2xl my-4 sm:my-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4 sm:mb-6 pb-3 sm:pb-4 border-b sticky top-0 bg-white z-10">
                <h2 className="text-xl sm:text-2xl font-bold">
                  {editingItem ? 'Edit Conveyor Item' : 'Add New Conveyor Item'}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowModal(false);
                  }}
                  className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <span className="text-2xl">✕</span>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    1. Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    placeholder="Nike Running Shoes"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    2. Description *
                  </label>
                  <textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    rows="3"
                    placeholder="Description of the item"
                  />
                </div>

                {/* Image Upload Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    3. Image Method *
                  </label>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-3">
                    <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer flex-1">
                      <input
                        type="radio"
                        value="url"
                        checked={uploadMethod === 'url'}
                        onChange={(e) => setUploadMethod(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm sm:text-base">Image URL</span>
                    </label>
                    <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer flex-1">
                      <input
                        type="radio"
                        value="upload"
                        checked={uploadMethod === 'upload'}
                        onChange={(e) => setUploadMethod(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm sm:text-base">Upload Image</span>
                    </label>
                  </div>

                  {uploadMethod === 'url' ? (
                    <input
                      type="url"
                      required={uploadMethod === 'url' && !editingItem}
                      value={formData.imagePath}
                      onChange={(e) => {
                        setFormData({ ...formData, imagePath: e.target.value });
                        setImagePreview(e.target.value);
                      }}
                      className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      placeholder="https://example.com/image.jpg"
                    />
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        required={uploadMethod === 'upload' && !editingItem}
                        className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Images over 512KB will be automatically compressed
                      </p>
                    </div>
                  )}

                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                      <div className="flex justify-center">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="max-h-32 sm:max-h-40 object-contain bg-white rounded border p-2"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Row Configuration */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      4. Rows (1-50) *
                    </label>
                    <select
                      required
                      value={formData.rows}
                      onChange={(e) => setFormData({ ...formData, rows: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    >
                      {generateRowsOptions()}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      5. Speed (seconds) *
                    </label>
                    <input
                      type="number"
                      required
                      min="0.1"
                      max="5"
                      step="0.1"
                      value={formData.speed}
                      onChange={(e) => setFormData({ ...formData, speed: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    />
                  </div>
                </div>

                {/* Dimensions */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      6. Width (px) *
                    </label>
                    <input
                      type="number"
                      required
                      min="50"
                      max="500"
                      value={formData.imageWidth}
                      onChange={(e) => setFormData({ ...formData, imageWidth: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      7. Height (px) *
                    </label>
                    <input
                      type="number"
                      required
                      min="100"
                      max="600"
                      value={formData.height}
                      onChange={(e) => setFormData({ ...formData, height: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    />
                  </div>
                </div>

                {/* Info and Order */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      8. Info *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.info}
                      onChange={(e) => setFormData({ ...formData, info: e.target.value })}
                      className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      placeholder="1,200 sold/min"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      9. Display Order *
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 sm:pt-6 border-t sticky bottom-0 bg-white">
                  <button
                    type="submit"
                    disabled={uploading}
                    className="flex-1 bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm sm:text-base font-medium transition-colors"
                  >
                    {uploading ? 'Processing...' : (editingItem ? 'Update Item' : 'Create Item')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      resetForm();
                      setShowModal(false);
                    }}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm sm:text-base font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConveyorItemManagement;
